import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import FieldRenderer from './FieldRenderer';
import { isFieldVisible, isInputField, READINESS_LEVELS } from './engine';
import { Eyebrow, Panel, READINESS_LABELS } from './ui';

/** Split a section's fields into cards, starting a new card at each heading. */
export function groupFields(fields) {
    const groups = [];
    let current = { heading: null, fields: [] };
    for (const field of fields || []) {
        if (field.type === 'heading') {
            if (current.heading || current.fields.length) groups.push(current);
            current = { heading: field, fields: [] };
        } else {
            current.fields.push(field);
        }
    }
    if (current.heading || current.fields.length) groups.push(current);
    return groups;
}

export function InfoBlock({ field }) {
    return (
        <details className="group rounded-xl border border-border bg-ink-50/70 px-4 py-3" open={!field.body || String(field.body).length < 240}>
            <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-ink-900">
                <InformationCircleIcon className="h-4 w-4 text-brand-700" aria-hidden="true" />
                {field.title || 'Guidance'}
                <span className="ml-auto text-xs font-normal text-ink-500 group-open:hidden">Show</span>
            </summary>
            {field.body && <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-ink-700">{field.body}</p>}
            {Array.isArray(field.bullets) && field.bullets.length > 0 && (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink-700">
                    {field.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
            )}
        </details>
    );
}

/** Lay out half-width fields two to a row. */
function FieldRows({ fields, render }) {
    const rows = [];
    for (let i = 0; i < fields.length; i += 1) {
        const field = fields[i];
        const next = fields[i + 1];
        if (field.width === 'half' && next && next.width === 'half') {
            rows.push([field, next]);
            i += 1;
        } else {
            rows.push([field]);
        }
    }
    return rows.map((row) => (row.length === 2 ? (
        <div key={row[0].key} className="grid grid-cols-1 gap-4 md:grid-cols-2">{row.map(render)}</div>
    ) : render(row[0])));
}

export default function SectionBlock({
    section, layout, answers, setAnswer, files, setFiles, readOnly, uploadsDisabled, challengeId, invalidKeys, commentsByField, hideFirstHeader
}) {
    const boxed = layout === 'workbook';
    const groups = groupFields(section.fields);

    const renderField = (field) => {
        if (field.type === 'info') return <InfoBlock key={field.title || field.body} field={field} />;
        if (!isInputField(field)) return null;
        if (!isFieldVisible(field, answers)) return null;
        return (
            <FieldRenderer
                key={field.key}
                field={field}
                answer={answers[field.key]}
                onChange={(value) => setAnswer(field.key, value)}
                readOnly={readOnly || (uploadsDisabled && field.type === 'file_upload')}
                files={(files || []).filter((f) => f.field_key === field.key)}
                onFilesChange={setFiles}
                challengeId={challengeId}
                boxed={boxed && field.type !== 'checklist'}
                invalid={invalidKeys && invalidKeys.has(field.key)}
                comments={commentsByField && commentsByField[field.key]}
            />
        );
    };

    return (
        <div className="space-y-5">
            {groups.map((group, gi) => {
                const showHeader = !(gi === 0 && hideFirstHeader);
                const eyebrow = group.heading ? group.heading.eyebrow : section.eyebrow;
                const title = group.heading ? group.heading.title : (boxed ? null : section.title);
                const description = group.heading ? group.heading.description : (boxed ? null : section.description);
                return (
                    <Panel key={gi}>
                        {showHeader && (eyebrow || title) && (
                            <header className="mb-4">
                                {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                                {title && <h2 className="mt-1 text-lg font-bold text-ink-950">{title}</h2>}
                                {description && <p className="mt-1 text-sm text-ink-600">{description}</p>}
                            </header>
                        )}
                        <div className={boxed ? 'space-y-3' : 'space-y-5'}>
                            <FieldRows fields={group.fields} render={renderField} />
                        </div>
                        {gi === groups.length - 1 && section.footnote && (
                            <p className="mt-4 text-xs text-ink-500">{section.footnote}</p>
                        )}
                    </Panel>
                );
            })}
        </div>
    );
}

export function ScorePanel({ score, className = '' }) {
    if (!score) return null;
    const scored = score.rows.filter((r) => !r.na && r.score).length;
    const level = scored ? score.level : null;
    return (
        <aside className={`rounded-2xl border border-brand-300/70 bg-white p-5 ${className}`} aria-label="Readiness score">
            <Eyebrow>Your AI readiness score</Eyebrow>
            <p className="mt-2 text-ink-950">
                <span className="text-4xl font-bold tracking-tight">{score.total}</span>
                <span className="text-xl text-ink-500"> / {score.max || 0}</span>
            </p>
            <p className="mt-1.5 text-xs text-ink-500">
                {scored} of {score.scored_count} area{score.scored_count === 1 ? '' : 's'} scored
                {score.na_count ? ` · ${score.na_count} marked N/A (removed from total)` : ''} · {score.percent}%
            </p>
            <ul className="mt-4 space-y-2.5">
                {score.rows.map((row) => (
                    <li key={row.key}>
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-ink-700">{row.short_label}</span>
                            <span className="font-semibold text-ink-900">{row.na ? 'N/A' : `${row.score || 0} / 5`}</span>
                        </div>
                        {!row.na && (
                            <div className="mt-1 h-1.5 w-3/4 rounded-full bg-ink-100">
                                <div className="h-full rounded-full bg-brand-700" style={{ width: `${((row.score || 0) / 5) * 100}%` }} />
                            </div>
                        )}
                    </li>
                ))}
            </ul>
            <div className="mt-4 border-t border-border pt-3">
                <p className="text-xs font-semibold text-ink-900">Readiness level</p>
                <div className="mt-2 grid grid-cols-4 overflow-hidden rounded-lg bg-ink-100 text-[11px]">
                    {READINESS_LEVELS.map((band) => (
                        <span key={band.key} className={`py-1.5 text-center ${level === band.key ? 'rounded-md bg-brand-700 font-semibold text-white' : 'text-ink-500'}`}>
                            {READINESS_LABELS[band.key]}
                        </span>
                    ))}
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-ink-500">Level is worked out from the % of the possible score when N/A is used.</p>
            </div>
            <p className="mt-3 border-t border-border pt-3 text-[11px] leading-relaxed text-ink-500">
                This score is a guide, not a guarantee that AI should be used.
            </p>
        </aside>
    );
}
